import type { ReactNode } from "react";
import {
  ALLOCATION_PRESETS,
  applyAllocation,
  applyCity,
  presetById,
  taxDragForSpend,
} from "../model/defaults.ts";
import { CITIES, REGIONS, SCHOOLING_WARNING, SCHOOL_PRESETS, accessMultiplier, cityById } from "../model/cities.ts";
import { LIMITS, MAX_PLANNING_AGE } from "../model/limits.ts";
import { LAKH } from "../model/types.ts";
import type { PlanInputs } from "../model/types.ts";
import { money, rupees } from "../lib/format.ts";
import { NumberField } from "./NumberField.tsx";
import { track } from "../lib/analytics.ts";

interface Props {
  inputs: PlanInputs;
  onChange: (next: PlanInputs) => void;
  notices: string[];
  /** Extra fieldsets (other savings) shown before the assumptions. */
  children?: ReactNode;
  /** Whether the lifestyle figure is the spending plan's total, and how to switch to it. */
  spendingPlan: {
    inUse: boolean;
    annualTotal: number;
    cityName: string;
    onUse: () => void;
    onOpen: () => void;
  };
}

/** Nested immutable update without pulling in a library. */
function patch(inputs: PlanInputs, section: keyof PlanInputs, values: object): PlanInputs {
  return { ...inputs, [section]: { ...(inputs[section] as object), ...values } };
}

const toPct = (v: number) => Math.round(v * 1000) / 10;
const fromPct = (v: number) => v / 100;
const toLakh = (v: number) => Math.round(v / LAKH);
const fromLakh = (v: number) => v * LAKH;

const ASSETS = ["Indian equity", "Global equity (INR)", "Indian debt", "Gold"] as const;

const INFLATION_FIELDS = [
  ["general", "General CPI", LIMITS.inflationGeneral],
  ["life", "Lifestyle basket", LIMITS.inflationBucket],
  ["school", "Schooling", LIMITS.inflationBucket],
  ["undergrad", "Undergraduate", LIMITS.inflationBucket],
  ["medical", "Medical and elder care", LIMITS.inflationBucket],
] as const;

export function Inputs({ inputs, onChange, notices, children, spendingPlan: sp }: Props) {
  const { household: h, spending: s, inflation: inf, markets: m } = inputs;

  const setChildren = (count: number) => {
    const ages = Array.from({ length: count }, (_, i) => h.childAges[i] ?? 8 + i * 3);
    onChange(patch(inputs, "household", { childAges: ages }));
  };

  const setLifestyle = (v: number) => {
    // A preset that pins its own withdrawal tax (an FD ladder, a debt fund) keeps it;
    // only the tier-based rate moves with the spending level.
    const pinned = presetById(m.presetId).taxDrag;
    onChange({
      ...patch(inputs, "spending", { lifestylePerYear: v, lifestylePricedLocally: false }),
      taxDrag: pinned ?? taxDragForSpend(v),
    });
  };

  const preset = presetById(m.presetId);
  const blend = (w: readonly number[]) =>
    w.reduce((sum, wt, i) => sum + wt * m.mu[i] * (1 - m.annualTax[i]), 0);
  const blended = blend(m.allocStart);
  const blendedEnd = blend(m.allocEnd);
  const blendedGross = m.allocStart.reduce((sum, w, i) => sum + w * m.mu[i], 0);
  const glides = m.glideYears > 0 && Math.abs(blendedEnd - blended) > 1e-6;

  const city = cityById(s.cityId);
  // What the slider figure becomes in the chosen city: only the locally priced
  // share of the basket moves with the city index (same formula as schedule.ts).
  const localLifestyle = s.lifestylePerYear * (s.localShare * city.index + (1 - s.localShare));
  const schoolWarning = SCHOOLING_WARNING[city.schooling];
  const accessCost = s.remoteAccessPerYear * accessMultiplier(h.childAges.length, h.parentCount);

  const maxHorizon = Math.max(LIMITS.horizonYears.min, MAX_PLANNING_AGE - h.adultAge);
  const horizonLimit = { ...LIMITS.horizonYears, max: maxHorizon, hint: `10 to ${maxHorizon}` };

  return (
    <form className="inputs" onSubmit={(e) => e.preventDefault()}>
      {notices.length > 0 && (
        <div className="notices" role="status">
          <strong>Adjusted to stay in range</strong>
          <ul>
            {notices.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </div>
      )}

      <p className="input-tip">
        Amounts accept shorthand: <kbd>4.5L</kbd>, <kbd>1.2cr</kbd> or <kbd>45,000</kbd>. Press
        Enter or click away to update the result.
      </p>

      <fieldset className="invested">
        <legend>How the corpus is invested</legend>

        <div className="field">
          <label htmlFor="allocation">Allocation</label>
          <select
            id="allocation"
            value={m.presetId}
            onChange={(e) => {
              onChange(applyAllocation(inputs, e.target.value));
              track("allocation-chosen", { preset: e.target.value });
            }}
          >
            {ALLOCATION_PRESETS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
          <small className="hint">{preset.note}</small>
        </div>

        <div className="mix">
          <div
            className="mix-bar"
            role="img"
            aria-label={`At retirement: ${ASSETS.map((a, i) => `${a} ${toPct(m.allocStart[i])}%`).join(", ")}`}
          >
            {m.allocStart.map((w, i) =>
              w > 0 ? <span key={ASSETS[i]} className={`mix-${i}`} style={{ flexGrow: w }} /> : null,
            )}
          </div>
          <ul className="mix-legend">
            {ASSETS.map((a, i) => (
              <li key={a}>
                <i className={`mix-${i}`} aria-hidden="true" />
                <span>{a}</span>
                <b>
                  {toPct(m.allocStart[i])}%
                  {glides && m.allocEnd[i] !== m.allocStart[i] ? ` → ${toPct(m.allocEnd[i])}%` : ""}
                </b>
              </li>
            ))}
          </ul>
        </div>

        <p className="readout">
          <span>Expected return{glides ? " at retirement" : ""}</span>
          <strong>
            {toPct(blended)}%{glides ? ` → ${toPct(blendedEnd)}%` : ""}
          </strong>
          <small className="hint">
            {blended < blendedGross - 1e-9
              ? `${toPct(blendedGross)}% before the tax charged every year on the interest, spent or not. Lifestyle inflation is ${toPct(inf.life)}%, so this portfolio loses ${toPct(inf.life - blended)}% of its purchasing power a year.`
              : `Against ${toPct(inf.life)}% lifestyle inflation${glides ? ", as the mix de-risks over " + m.glideYears + " years" : ""}. Tax falls only on the gain in what you withdraw, so the rest keeps compounding.`}
          </small>
        </p>
      </fieldset>

      <fieldset>
        <legend>What you will spend, in today's rupees</legend>

        <div className="field">
          <label htmlFor="lifestyle">Lifestyle only, per year</label>
          <p className={`source ${sp.inUse ? "in-use" : ""}`} role="status">
            {sp.inUse ? (
              <>
                ✓ Using your spending plan ({sp.cityName}).{" "}
                <button type="button" className="link" onClick={sp.onOpen}>Edit plan</button>
                <span className="source-note">
                  Moving the slider switches to your own amount; the plan itself is kept.
                </span>
              </>
            ) : (
              <>
                Using your own amount. Your spending plan totals {money(sp.annualTotal)} a year.{" "}
                <button type="button" className="link" onClick={sp.onUse}>Use plan</button>
              </>
            )}
          </p>
          <input
            id="lifestyle"
            type="range"
            min={12}
            max={200}
            step={2}
            value={Math.min(200, Math.max(12, toLakh(s.lifestylePerYear)))}
            onChange={(e) => setLifestyle(fromLakh(+e.target.value))}
          />
          <small className="hint">
            {s.lifestylePricedLocally
              ? `Already priced for ${city.name}, so it is used as it is. If you move the slider, enter a Bengaluru-price figure instead and it will be converted for you.`
              : city.index === 1
                ? "What your household spends on day-to-day living in a year, at today's prices."
                : `Enter what this lifestyle would cost in Bengaluru today. In ${city.name} it comes to about ${money(localLifestyle)} a year, and that is the figure the plan uses.`}{" "}
            Leave out school fees, university, parents' care, health insurance and tax: they are
            counted separately.
          </small>
        </div>

        <NumberField
          indent
          label="Or type the exact amount, per year"
          unit="inr"
          value={s.lifestylePerYear}
          limit={LIMITS.lifestylePerYear}
          hint={`The slider covers ₹12 L to ₹2 cr; type anything from ${LIMITS.lifestylePerYear.hint}.`}
          onCommit={setLifestyle}
        />

        <div className="field">
          <label htmlFor="city">Where you will live</label>
          <select
            id="city"
            value={s.cityId}
            onChange={(e) =>
              onChange({ ...inputs, spending: applyCity(s, e.target.value) })
            }
            aria-describedby={s.lifestylePricedLocally ? "city-local-warning" : undefined}
          >
            {REGIONS.map((region) => (
              <optgroup key={region} label={region}>
                {CITIES.filter((c) => c.region === region).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} — {Math.round(c.index * 100)}
                    {c.index === 1 ? " (baseline)" : ""}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <small className="hint">
            {city.note}
            {!city.measured && " Index estimated rather than measured."}
          </small>
          {s.lifestylePricedLocally && (
            <p className="warn" id="city-local-warning">
              Your lifestyle figure came from the spending planner priced for a specific city. If you
              change city here, re-run the planner so the figure matches.
            </p>
          )}
        </div>

        <div className="city-profile">
          <dl>
            <div>
              <dt>Local costs</dt>
              <dd>
                {city.index === 1
                  ? "Baseline"
                  : `${Math.round(Math.abs(1 - city.index) * 100)}% ${city.index < 1 ? "cheaper" : "dearer"} than Bengaluru`}
              </dd>
            </div>
            <div>
              <dt>Super-specialty care</dt>
              <dd>{city.tertiaryCare}</dd>
            </div>
            {accessCost > 0 && (
              <div>
                <dt>Getting to services</dt>
                <dd className="cost">{money(accessCost)}/yr</dd>
              </div>
            )}
          </dl>
          {accessCost > 0 && (
            <p>
              Living here is cheaper, but the specialist appointments are somewhere else. That
              travel is charged as its own line and inflates at the medical rate, so it grows
              faster than the saving does — and faster still with elderly dependents.
            </p>
          )}
        </div>

        {h.childAges.length > 0 && (
          <>
            {schoolWarning && <p className="warn">{schoolWarning}</p>}

            <div className="field">
              <label htmlFor="school-preset">Schooling available in {city.name}</label>
              <select
                id="school-preset"
                value={
                  SCHOOL_PRESETS[city.schooling].find((o) => o.perYear === s.schoolPerChildPerYear)
                    ?.label ?? "custom"
                }
                onChange={(e) => {
                  const preset = SCHOOL_PRESETS[city.schooling].find((o) => o.label === e.target.value);
                  if (preset) onChange(patch(inputs, "spending", { schoolPerChildPerYear: preset.perYear }));
                }}
              >
                {SCHOOL_PRESETS[city.schooling].map((o) => (
                  <option key={o.label} value={o.label}>
                    {o.label} — {money(o.perYear)}/yr
                  </option>
                ))}
                <option value="custom">Custom</option>
              </select>
            </div>

            <NumberField
              indent
              label="Schooling, all-in per child per year"
              unit="inr"
              value={s.schoolPerChildPerYear}
              limit={LIMITS.schoolPerChildPerYear}
              hint={`${rupees(s.schoolPerChildPerYear)} — fees plus transport, activities, tutoring. Allowed: ${LIMITS.schoolPerChildPerYear.hint}`}
              onCommit={(v) => onChange(patch(inputs, "spending", { schoolPerChildPerYear: v }))}
            />

            <div className="field">
              <label htmlFor="undergrad">Undergraduate, per child per year</label>
              <select
                id="undergrad"
                value={s.undergradPerYear}
                onChange={(e) => onChange(patch(inputs, "spending", { undergradPerYear: +e.target.value }))}
              >
                <option value={75 * LAKH}>Abroad — ₹75 L/yr</option>
                <option value={40 * LAKH}>Abroad, cheaper — ₹40 L/yr</option>
                <option value={15 * LAKH}>India, private — ₹15 L/yr</option>
                <option value={5 * LAKH}>India, public — ₹5 L/yr</option>
                <option value={0}>None</option>
              </select>
              <small className="hint">
                {s.undergradYears} years starting at age {s.undergradStartAge}.
              </small>
            </div>
          </>
        )}

        {h.parentCount > 0 && (
          <NumberField
            label="Parents' care at peak, per year (all of them)"
            unit="inr"
            value={s.elderRamp[s.elderRamp.length - 1]?.perYear ?? 0}
            limit={LIMITS.elderPerYear}
            hint={`Ramps up from ${rupees(s.elderRamp[0]?.perYear ?? 0)} while they are semi-dependent, ending at age ${s.elderEndAge}. Allowed: ${LIMITS.elderPerYear.hint}`}
            onCommit={(v) => {
              const ramp = s.elderRamp.map((step, i) =>
                i === s.elderRamp.length - 1 ? { ...step, perYear: v } : step,
              );
              onChange(patch(inputs, "spending", { elderRamp: ramp }));
            }}
          />
        )}

        <NumberField
          label="Health cover for your household, per year"
          unit="inr"
          value={s.healthCoverPerYear}
          limit={LIMITS.healthCoverPerYear}
          onCommit={(v) => onChange(patch(inputs, "spending", { healthCoverPerYear: v }))}
        />
      </fieldset>

      <fieldset>
        <legend>Who is retiring</legend>

        <NumberField
          label="Your age at retirement"
          value={h.adultAge}
          limit={LIMITS.adultAge}
          onCommit={(v) => onChange(patch(inputs, "household", { adultAge: v }))}
        />

        <div className="field">
          <label htmlFor="children">Children</label>
          <select id="children" value={h.childAges.length} onChange={(e) => setChildren(+e.target.value)}>
            {[0, 1, 2, 3, 4].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>

        {h.childAges.map((age, i) => (
          <NumberField
            key={i}
            indent
            label={`Child ${i + 1} age at retirement`}
            value={age}
            limit={LIMITS.childAge}
            hint={
              age >= s.undergradStartAge
                ? `Already ${s.undergradStartAge} or older, so schooling is done and undergrad starts straight away.`
                : `Allowed: ${LIMITS.childAge.hint}`
            }
            onCommit={(v) => {
              const next = [...h.childAges];
              next[i] = v;
              onChange(patch(inputs, "household", { childAges: next }));
            }}
          />
        ))}

        <div className="field">
          <label htmlFor="parents">Dependent parents</label>
          <select
            id="parents"
            value={h.parentCount}
            onChange={(e) => onChange(patch(inputs, "household", { parentCount: +e.target.value }))}
          >
            {[0, 1, 2, 3, 4].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>

        {h.parentCount > 0 && (
          <NumberField
            indent
            label="Their average age at retirement"
            value={h.parentAge}
            limit={LIMITS.parentAge}
            onCommit={(v) => onChange(patch(inputs, "household", { parentAge: v }))}
          />
        )}

        <NumberField
          label="Years until you retire"
          value={inputs.yearsToRetirement}
          limit={LIMITS.yearsToRetirement}
          onCommit={(v) => onChange({ ...inputs, yearsToRetirement: v })}
        />

        <NumberField
          label="Years the money must last"
          value={inputs.horizonYears}
          limit={horizonLimit}
          hint={`To age ${h.adultAge + inputs.horizonYears}. Capped so the plan ends by ${MAX_PLANNING_AGE}.`}
          onCommit={(v) => onChange({ ...inputs, horizonYears: v })}
        />
      </fieldset>

      {children}

      <details className="advanced">
        <summary>Assumptions — change these if you disagree</summary>

        <fieldset>
          <legend>Inflation</legend>
          {INFLATION_FIELDS.map(([key, label, limit]) => (
            <NumberField
              key={key}
              label={label}
              value={inf[key]}
              limit={limit}
              suffix="%"
              toDisplay={toPct}
              fromDisplay={fromPct}
              onCommit={(v) => onChange(patch(inputs, "inflation", { [key]: v }))}
            />
          ))}
        </fieldset>

        <fieldset>
          <legend>Returns</legend>
          {ASSETS.map((label, i) => (
            <NumberField
              key={label}
              label={label}
              value={m.mu[i]}
              limit={LIMITS.assetReturn}
              suffix="%"
              toDisplay={toPct}
              fromDisplay={fromPct}
              hint={`${toPct(m.sigma[i])}% volatility, ${toPct(m.allocStart[i])}% of the portfolio at retirement. Allowed: ${LIMITS.assetReturn.hint}`}
              onCommit={(v) => {
                const mu = [...m.mu] as typeof m.mu;
                mu[i] = v;
                onChange(patch(inputs, "markets", { mu }));
              }}
            />
          ))}
        </fieldset>

        <fieldset>
          <legend>Method</legend>

          <NumberField
            label="Tax drag on withdrawals"
            value={inputs.taxDrag}
            limit={LIMITS.taxDrag}
            suffix="%"
            toDisplay={toPct}
            fromDisplay={fromPct}
            hint={`Below the headline 12.5% LTCG rate because a mature SWP is mostly return of capital. Allowed: ${LIMITS.taxDrag.hint}`}
            onCommit={(v) => onChange({ ...inputs, taxDrag: v })}
          />

          <div className="field checkbox">
            <input
              id="guardrails"
              type="checkbox"
              checked={inputs.guardrails}
              onChange={(e) => onChange({ ...inputs, guardrails: e.target.checked })}
            />
            <label htmlFor="guardrails">Cut discretionary spending in bad years</label>
            <small className="hint">
              Up to 20% off when the withdrawal rate drifts above plan. Turning this off is the
              single most expensive choice in the model.
            </small>
          </div>

          <div className="field">
            <label htmlFor="sims">Simulation paths</label>
            <select
              id="sims"
              value={inputs.simulations}
              onChange={(e) => onChange({ ...inputs, simulations: +e.target.value })}
            >
              <option value={5000}>5,000 — fastest</option>
              <option value={20000}>20,000 — default</option>
              <option value={50000}>50,000 — slowest, steadiest</option>
            </select>
          </div>
        </fieldset>
      </details>
    </form>
  );
}
